import { AlertTriangle, Gauge, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, buttonClassName, EmptyState } from '@/components/ui';
import { isApiError, planRefusalOf } from '@/lib/api';

export interface ErrorStateProps {
  /** The thrown error; an ApiError yields a precise message. */
  error: unknown;
  /** Retry handler (usually `refetch` from the query). */
  onRetry?: () => void;
  /** Override the default title. */
  title?: string;
}

/** Turn an unknown error into a user-readable, actionable message. */
function describe(error: unknown): string {
  if (isApiError(error)) {
    if (error.isForbidden) return 'Você não tem permissão para ver estes dados.';
    if (error.isServer) return 'O serviço está instável no momento. Tente novamente em instantes.';
    return error.message;
  }
  return 'Não foi possível carregar os dados. Verifique sua conexão e tente novamente.';
}

/**
 * A recusa do PLANO não é falha: é a borda dizendo que a superfície é paga
 * (403 `plan_capability_required`) ou que a cota gratuita do mês acabou (429
 * `plan_quota_exceeded`). O que sara as duas é o upgrade, não a tentativa —
 * por isso o "Tentar novamente" sai e o CTA de /planos entra. A âncora é o
 * CÓDIGO do envelope: a mensagem da capability fala para o log, não para a
 * tela ("capability 'decks' não está incluída...").
 */
const PLAN_COPY = {
  capability: {
    icon: <Lock />,
    title: 'Este recurso faz parte do Pro',
    description: 'O plano gratuito cobre o chat e o acervo público. Este recurso está no Pro.',
  },
  quota: {
    icon: <Gauge />,
    title: 'A cota do mês acabou',
    description:
      'Você usou todos os tokens do plano gratuito deste mês. A cota renova no dia 1º — ou continue agora com o Pro.',
  },
} as const;

/**
 * Uniform failure surface for a failed query: acknowledge, explain, offer retry.
 * Reused on every list and detail screen so errors never read as a blank page.
 */
export function ErrorState({ error, onRetry, title = 'Falha ao carregar' }: ErrorStateProps): JSX.Element {
  const planRefusal = planRefusalOf(error);
  if (planRefusal) {
    const copy = PLAN_COPY[planRefusal];
    return (
      <EmptyState
        icon={copy.icon}
        title={copy.title}
        description={copy.description}
        action={
          <Link to="/planos" className={buttonClassName('primary')}>
            Conhecer o Pro
          </Link>
        }
      />
    );
  }

  return (
    <EmptyState
      tone="error"
      icon={<AlertTriangle />}
      title={title}
      description={describe(error)}
      action={
        onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Tentar novamente
          </Button>
        ) : undefined
      }
    />
  );
}
